/**
 * @namespace source.folders
 */

/**
 * @typedef ListSettings
 * @type Object
 * @property {object} paging
 * @property {string} paging.cursor
 * @property {number} paging.size
 * @property {object} sort
 * @property {string} sort.order
 * @property {string} sort.field
 */

/**
 * @typedef List
 * @type Object
 * @property {Array} data
 * @property {Object} paging
 * @property {string} paging.cursor
 * @property {number} paging.size
 */

import { toFolder } from './mappers/folder';
import { toError } from './parsers/error';
export function initFolders(settings, http) {
  function mapFolderList(res, token) {
    res.data = (res.data.folders || []).map(function (folder) {
      return toFolder({
        ...folder,
        token
      }, settings);
    });
    return res;
  }
  const defaultPaging = {
    size: 50,
    cursor: null
  };
  const defaultSort = {
    order: 'date',
    direction: 'desc'
  };

  /**
   * @summary Return the list of folders
   * @memberof source.folders
   * @param {String} folderId
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List[]} List with Folders
   * @reject {Error}
   */
  function list(folderId, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      page_size: paging.size,
      cursor: paging.cursor,
      parent_folder_id: folderId || null
    };
    queryParams.order = sort.direction === 'desc' ? `-${sort.order}` : sort.order;
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/go/site/media/folders/list`, queryParams, {
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapFolderList(response, options.siteMediaToken);
    }).catch(toError);
  }
  function removeFolder(folderId, options) {
    const url = `${settings.apiUrl}/site/media/folders/${folderId}`;
    const postOptions = {
      headers: options.headers || {}
    };
    if (settings.namespace) {
      postOptions.ns = settings.namespace;
    }
    return http.del(url, null, null, postOptions).then(function (res) {
      res.data = {
        id: folderId
      };
      return res;
    }).catch(toError);
  }

  /**
   * @summary Removes the list of folders with given ids
   * @memberof source.folders
   * @param {string[]} folderIds Array of folder ids
   * @param {Object} options
   * @returns {Promise[]}
   */
  function remove(folderIds, options) {
    options = options || {};
    return folderIds.map(function (folderId) {
      return removeFolder(folderId, options);
    });
  }

  /**
   * @summary Return list of folders found by text query
   * @memberof source.folders
   * @param {string} folderId
   * @param {string} query
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function search(folderId, query, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      query,
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType,
      parent_folder_id: folderId || null,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order
    };
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/site/media/folders/search`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapFolderList(response, options.thumbnails, options.siteMediaToken);
    }).catch(toError);
  }
  return {
    list,
    remove,
    search
  };
}